import React, { JSX, ReactNode } from 'react';

interface State {
  visible: boolean;
  height: number;
  content: ReactNode;
}

export interface SetBottomSheetAction {
  type: 'SET_BOTTOMSHEET';
  setHeight: number;
  setContent: ReactNode;
  setVisible?: boolean;
}

export interface SetVisibleAction {
  type: 'SET_VISIBLE';
  setVisible: boolean;
}

type Action = SetBottomSheetAction | SetVisibleAction;

type Dispatch = (action: Action) => void;

interface BottomSheetContextData {
  state: State;
  dispatch: Dispatch;
}

const BottomSheetContext = React.createContext<BottomSheetContextData | undefined>(undefined);

function bottomSheetReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_BOTTOMSHEET':
      return {
        ...state,
        height: action.setHeight,
        content: action.setContent,
        visible: action.setVisible ? action.setVisible : state.visible
      };
    case 'SET_VISIBLE':
      return {
        ...state,
        visible: action.setVisible
      };
    default:
      throw new Error(`Unhandled action type`);
  }
}

function BottomSheetProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = React.useReducer(bottomSheetReducer, {
    visible: false,
    height: 400,
    content: <></>
  });

  return <BottomSheetContext.Provider value={{ state, dispatch }}>{children}</BottomSheetContext.Provider>;
}

function useBottomSheet(): BottomSheetContextData {
  const context = React.useContext(BottomSheetContext);
  if (context === undefined) {
    throw new Error('useBottomSheet must be used within a BottomSheetProvider');
  }
  return context;
}

export { BottomSheetProvider, useBottomSheet };
